(ns ycweb.app.dashboard.planning.suppliers
  (:require
   [rum.core :as rum]
   [ycweb.app.admin.wholesale.common :as w.cmn]
   [ycweb.app.dashboard.planning.common :as planning.cmn]
   [ycweb.app.dashboard.presence :as presence]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.datatables.datatables :as datatables]
   [ycweb.components.editor.base :as editor]
   [ycweb.components.flow :as flow]
   [ycweb.components.headings :as headings]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.transitions :as t]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]
   [ycweb.util.date :as ud]
   [ycweb.util.urlstate :as urlstate]
   [ycweb.components.grid :as grid]))

(def datatable-key :stockorder.planning.suppliers/list)

(def ordergroup-editor-key :stockorder.planning.suppliers/editor)

(def closedstart-editor-key :stockorder.planning.suppliers/closedstart-editor)

(def closedend-editor-key :stockorder.planning.suppliers/closedend-editor)

(def datatable-fns
  {:column-fns
   {:unix-to-millis
    (fn [_app-atom _row _col unix-seconds]
        ;; Convert unix timestamp from seconds to milliseconds
      (when unix-seconds
        (* unix-seconds 1000)))}})

(def datatable-columns
  [{:title "ID"
    :key :id
    :align-class :u-text-center}
   {:title "Company Name"
    :key :companyname}
   {:title "Contact Name"
    :key :contactname}
   {:title "Contact Email"
    :key :contactemail}
   {:title "Contact Tel"
    :key :contacttel}
   {:title "Order Group"
    :key :ordergroup}
   {:title "Festive Closed Start"
    :key :closedstart-unix
    :edit-field :ecommerce-suppliers/closedstart
    :type :date
    :dateformat "dd MMM yyyy"
    :align-class :u-text-center
    :render {:logic-k :unix-to-millis}}
   {:title "Festive Closed End"
    :key :closedend-unix
    :edit-field :ecommerce-suppliers/closedend
    :type :date
    :dateformat "dd MMM yyyy"
    :align-class :u-text-center
    :render {:logic-k :unix-to-millis}}])

(defn datatable-config [app-atom]
  {:table-name datatable-key
   :row-id :id
   :data-source
   {:end-point :stockorder.planning.suppliers/list}
   :select? {:style :os}
   :options
   {:order :asc
    :sort-title "ID"
    :sticky-header? true
    :paging? true
    :pagination {:page 0 :page-size 30}}
   :buttons
   [:refresh
    {:text "Deselect All"
     :action :stockorder.planning.suppliers/deselect-all
     :config {:table-name datatable-key}
     :attr {:small true}}
    {:text "Edit Order Group"
     :editor ordergroup-editor-key
     :extends :edit
     :multi-select true
     :attr {:small true}}
    {:text "Edit Closed Start"
     :editor closedstart-editor-key
     :extends :edit
     :multi-select true
     :attr {:small true}}
    {:text "Edit Closed End"
     :editor closedend-editor-key
     :extends :edit
     :multi-select true
     :attr {:small true}}]
   :column-defs datatable-columns})

(def ordergroup-editor-config
  {:editor-name ordergroup-editor-key
   :datatable datatable-key
   :mode :read
   :end-points
   {:edit :stockorder.planning.suppliers/edit}
   :fields
   [{:name :ecommerce-suppliers/ordergroup
     :label "Order Group:"
     :type :text
     :attr {:placeholder "Order Group"}}]})

(def closedstart-editor-config
  {:editor-name closedstart-editor-key
   :datatable datatable-key
   :mode :read
   :end-points
   {:edit :stockorder.planning.suppliers/edit}
   :fields
   [{:name :ecommerce-suppliers/closedstart
     :label "Festive Closed Start:"
     :type :date
     :attr {:placeholder "Select date"}}]})

(def closedend-editor-config
  {:editor-name closedend-editor-key
   :datatable datatable-key
   :mode :read
   :end-points
   {:edit :stockorder.planning.suppliers/edit}
   :fields
   [{:name :ecommerce-suppliers/closedend
     :label "Festive Closed End:"
     :type :date
     :attr {:placeholder "Select date"}}]})

(defn- unix-seconds->datetime
  "Convert Unix timestamp (seconds) to DateTime object for the datepicker.
   Returns nil if the input is nil or 0 (so datepicker defaults to today)."
  [unix-seconds]
  (when (and unix-seconds (pos? unix-seconds))
    (ud/from-millis (* unix-seconds 1000))))

(defn- transform-date-fields
  "Transform date fields for both display and editing:
   - Rename :closedstart -> :closedstart-unix (for datatable display)
   - Add :ecommerce-suppliers/closedstart (DateTime for editor/datepicker)
   This prevents cell-data from finding :closedstart when it strips the namespace."
  [row]
  (let [closedstart-val (:closedstart row)
        closedend-val (:closedend row)]
    (cond-> (dissoc row :closedstart :closedend)
      closedstart-val
      (-> (assoc :closedstart-unix closedstart-val)
        (assoc :ecommerce-suppliers/closedstart (unix-seconds->datetime closedstart-val)))

      closedend-val
      (-> (assoc :closedend-unix closedend-val)
        (assoc :ecommerce-suppliers/closedend (unix-seconds->datetime closedend-val))))))

(defmethod comms/receive :stockorder.planning.suppliers/list
  [app-atom {:keys [rows]}]
  (let [transformed-rows (map transform-date-fields rows)]
    (datatables/post-comms app-atom datatable-key
      {:rows transformed-rows})))

(defmethod comms/receive :stockorder.planning.suppliers/edit [app-atom e]
  ;; Call post-submit for all three editors since they share the same endpoint
  ;; This ensures whichever editor was used gets properly reset
  (let [result1 (editor/post-submit app-atom ordergroup-editor-key e)
        result2 (editor/post-submit app-atom closedstart-editor-key e)
        result3 (editor/post-submit app-atom closedend-editor-key e)
        success (or (:success result1) (:success result2) (:success result3))]
    (when success
      ;; Transform the :rows to use the datatable's expected keys (:closedstart-unix, etc.)
      (let [transformed-response (update e :rows #(map transform-date-fields %))]
        (datatables/update-rows app-atom datatable-key transformed-response)))))

(rum/defc suppliers-page < rum/reactive [app-atom]
  [:div
   [:div
    {:class (u/bem :flex-grid-group [:flush :align-left :valign-middle])}
    [:div
     {:class (u/bem :flex-grid-block [:fluid :flush :grow-yes])}
     [:div.flex-grid-content
      (headings/heading "Suppliers Management" {:type :h1 :size :large})]]
    [:div
     {:class (u/bem :flex-grid-block [:fluid])}
     [:div.flex-grid-content
      (buttons/button-web "Back to Planning Dashboard"
        (merge
          (urlstate/href-props app-atom "planning-new")
          {:style :secondary
           :size :default}))]]
    [:div
     {:class (u/bem :flex-grid-block [:fluid])}
     [:div.flex-grid-content
      (buttons/button-web "Try new layout"
        (merge
          (w.cmn/try-new-layout-props)
          {:style :light
           :size :default}))]]]
   [:div.u-min-height-160
    (datatables/table app-atom datatable-key datatable-fns)
    (toaster/loading-overlay app-atom datatable-key)]])

(defmethod flow/render-screen :admin.planning-new/suppliers [app-atom _]
  (planning.cmn/with-loading-overlay app-atom
    (cmn/permission-lock app-atom "SiteAdmin"
      (suppliers-page app-atom))))

(defmethod t/transition-open :admin.planning-new/suppliers [app-atom _ params]
  (w.cmn/redirect-to-new-layout-if-needed! app-atom "planning" params))

(defmethod grid/action :stockorder.planning.suppliers/deselect-all [app-atom config]
  (datatables/deselect-rows app-atom (:table-name config)))

(defmethod t/transition-after-open :admin.planning-new/suppliers [app-atom _ _]
  (presence/open app-atom {:context/ident :stockorder.planning.suppliers/dashboard})
  (datatables/initiate app-atom (datatable-config app-atom) datatable-fns)
  (editor/initiate app-atom ordergroup-editor-config)
  (editor/initiate app-atom closedstart-editor-config)
  (editor/initiate app-atom closedend-editor-config))

